import React, { useEffect, useMemo, useState } from "react";
import {
  Combobox,
  Group,
  InputBase,
  ScrollArea,
  Text,
  useCombobox,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { DiscountPreset } from "../../../../../types/discounts";
import { formatDiscountValue } from "../../../../../utils/discountFormatting";
import {
  findDiscountPresetByName,
  validateDiscountName,
} from "../../../../../utils/discountPresetUtils";

/** What the picker currently holds. */
export type DiscountPickerValue =
  /** An active preset from the location's catalog. */
  | { kind: "preset"; preset: DiscountPreset }
  /** "+ Create '{name}'": a new preset, created when the discount is applied. */
  | { kind: "create"; name: string }
  /** A discount already on the ticket with no active preset (legacy, unlinked,
   *  or archived) — shown while it's being changed. */
  | { kind: "existing"; name: string };

export interface DiscountPresetPickerProps {
  presets: DiscountPreset[];
  value: DiscountPickerValue | null;
  onChange: (value: DiscountPickerValue | null) => void;
  /** Offer "+ Create" for an unknown name (settingsDiscounts.canManagePresets). */
  canCreate: boolean;
  loading?: boolean;
  /** Helper text under the input. */
  description?: React.ReactNode;
  error?: React.ReactNode;
  disabled?: boolean;
}

const CREATE_OPTION = "__create_discount_preset__";

export const pickerValueLabel = (value: DiscountPickerValue | null): string => {
  if (!value) return "";
  return value.kind === "preset" ? value.preset.name : value.name;
};

/**
 * Searchable, creatable discount picker (Mantine `useCombobox`, after
 * ProductLookup / ClientLookup). Lists the location's presets with their
 * value; typing a name that doesn't exist offers "+ Create '{name}'" when the
 * user may manage presets. Choosing Create only records the intent — the host
 * creates the preset (with the type/amount entered alongside) on Apply.
 */
export default function DiscountPresetPicker({
  presets,
  value,
  onChange,
  canCreate,
  loading = false,
  description,
  error,
  disabled = false,
}: DiscountPresetPickerProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });
  const selectedLabel = pickerValueLabel(value);
  const [search, setSearch] = useState(selectedLabel);

  // Follow external changes (modal reset, "Change" prefill, create → preset).
  useEffect(() => {
    setSearch(selectedLabel);
  }, [selectedLabel]);

  const query = search.trim();
  // While the input just shows the current selection, list everything.
  const isShowingSelection = value !== null && search === selectedLabel;
  const filtered = useMemo(() => {
    if (isShowingSelection || !query) return presets;
    const needle = query.toLocaleLowerCase();
    return presets.filter((preset) =>
      preset.name.toLocaleLowerCase().includes(needle),
    );
  }, [presets, query, isShowingSelection]);
  const showCreate =
    canCreate &&
    !isShowingSelection &&
    query.length > 0 &&
    !findDiscountPresetByName(presets, query) &&
    validateDiscountName(query) === null;

  const handleOptionSubmit = (optionValue: string) => {
    if (optionValue === CREATE_OPTION) {
      onChange({ kind: "create", name: query });
      setSearch(query);
    } else {
      const preset = presets.find((p) => p.id === optionValue);
      if (preset) {
        onChange({ kind: "preset", preset });
        setSearch(preset.name);
      }
    }
    combobox.closeDropdown();
  };

  const emptyMessage = loading
    ? "Loading discounts…"
    : presets.length === 0
      ? canCreate
        ? "Type a name to create your first discount."
        : "No discounts set up yet. A manager can add them in Settings → Discounts."
      : "No matching discounts.";

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSubmit}>
      <Combobox.Target>
        <InputBase
          label="Discount"
          required
          placeholder={
            canCreate ? "Search or create a discount" : "Search discounts"
          }
          value={search}
          description={description}
          error={error}
          disabled={disabled}
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown();
            // Leaving without choosing restores the current selection.
            if (value) setSearch(selectedLabel);
          }}
          onChange={(event) => {
            const next = event.currentTarget.value;
            setSearch(next);
            if (value && next !== selectedLabel) onChange(null);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea.Autosize mah={260} type="scroll">
            {filtered.map((preset) => (
              <Combobox.Option
                value={preset.id}
                key={preset.id}
                active={
                  value?.kind === "preset" && value.preset.id === preset.id
                }
              >
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Text size="sm">{preset.name}</Text>
                  <Text size="sm" c="dimmed">
                    {formatDiscountValue(preset.discount_type, preset.amount)}
                  </Text>
                </Group>
              </Combobox.Option>
            ))}
            {showCreate && (
              <Combobox.Option value={CREATE_OPTION}>
                <Group gap={6} wrap="nowrap">
                  <IconPlus size={14} />
                  <Text size="sm">{`Create "${query}"`}</Text>
                </Group>
              </Combobox.Option>
            )}
            {filtered.length === 0 && !showCreate && (
              <Combobox.Empty>{emptyMessage}</Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
